import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { getGenderValue } from "@utils/helpers";
import { formatAddress, getDifferenceInYearsAndMonths } from "@utils/utils";
import { format, parseISO } from "date-fns";
import { useState } from "react";

import { Button, Paragraph } from "@fronterahealth/frontera-ui-components";

import GeneralInfoTable from "@pages/EvaluationDetails/Components/GeneralInfoTable";
import { EvaluationClientDetails } from "@pages/EvaluationDetails/EvaluationDetailsSubPages/EvaluationClientDetails";
import { useReport } from "@providers/ReportProvider";

interface GeneralInfoSectionProps {
  allowInLineEdit: boolean;
}

export const GeneralInfoSection: React.FC<GeneralInfoSectionProps> = ({ allowInLineEdit }) => {
  const { report } = useReport();
  const clientDetails = report?.reportClientDetails;
  const clientName = `${clientDetails?.clientFirstName ?? ""} ${clientDetails?.clientLastName ?? ""}`;

  const isoString = new Date().toISOString();
  const birthDateOrDate = clientDetails?.birthDate || isoString;
  const completionDateOrDate = report?.completionDate || isoString;
  const birthDateFormatted = format(parseISO(birthDateOrDate), "MM/dd/yyyy");
  const reportDateFormatted = format(parseISO(completionDateOrDate), "MM/dd/yyyy");
  const ageAtEvaluation = getDifferenceInYearsAndMonths(birthDateOrDate, report?.createdDate);
  const formattedGender = getGenderValue(clientDetails?.gender) || "";

  const [isEditing, setIsEditing] = useState(false);
  const sectionHeading = "General Information";

  return (
    <section
      data-testid={`report-section-${sectionHeading}`}
      className={`px-8 py-6 mb-4 pageBreak rounded-3xl bg-white`}
      id="GeneralInformation"
    >
      <div className={`flex  items-center justify-between`}>
        <div
          data-testid="section-title-row"
          className="flex items-center relative group w-full"
          id={`GeneralInformation-${sectionHeading.replace(/\s+/g, "-")}`} // replacing all the spaces with "-"
          data-sticky-nav-header
        >
          <span className="border p-2 rounded-sm mr-2">
            <DocumentTextIcon className="h-6 w-6" />
          </span>
          <Paragraph
            displayType="loud"
            colorType="primary"
            data-dynamic-section
            data-testid="section-title-input-paragraph"
            className={`${isEditing ? "cursor-pointer" : ""}`}
            onClick={() => isEditing && setIsEditing(true)}
          >
            {sectionHeading}
          </Paragraph>
        </div>
        {allowInLineEdit && (
          <Button
            onClick={() => setIsEditing(!isEditing)}
            appearance="link"
            text={isEditing ? "Done Editing" : "Edit"}
            className="ml-2"
          />
        )}
      </div>
      {isEditing ? (
        <>
          <EvaluationClientDetails renderType="report-page-component" onEditingDone={() => setIsEditing(false)} />
        </>
      ) : (
        <GeneralInfoTable
          clientName={clientName}
          dateOfBirth={birthDateFormatted}
          gender={
            formattedGender === "Gender identity is not listed" ? clientDetails?.specifyGender ?? "" : formattedGender
          }
          age={ageAtEvaluation}
          address={formatAddress({
            addressLine1: clientDetails?.address ?? "",
            addressLine2: clientDetails?.addressLine2 ?? "",
            state: clientDetails?.state ?? "",
            city: clientDetails?.city ?? "",
            zip: clientDetails?.zip ?? "",
          })}
          evaluationDate={reportDateFormatted}
          examiner={report?.reportWriter || ""}
        />
      )}
    </section>
  );
};
